/**
 * Analytics Types for renderer process
 * Shared types used by the useAnalytics hook and analytics API
 */

import type { DatabaseEngineId } from '../engines/engineTypes';
import type { CountBucket } from './telemetryBuckets';

/**
 * Subscription/license tier attached to every analytics event.
 *
 * Mirrors the IPC contract's `SubscriptionType` in
 * `electron/analytics/analyticsTypes.ts` — the two are structurally identical
 * by design so the renderer can compute the tier and push it to the main
 * process via `electronAPI.analytics.setContext`.
 */
export type SubscriptionType = 'personal' | 'pro' | 'trial';

/**
 * Database engine type for analytics events.
 *
 * Carries the app's canonical engine id (`DatabaseEngineId`) through to
 * telemetry AS-IS — no allowlist, no coercion. `'unknown'` is used ONLY when
 * the engine is genuinely absent/untyped; never coerce a real engine to a
 * different engine name (a past `'oracle' → 'mssql'` fallback silently
 * mislabeled every Oracle event).
 */
export type AnalyticsEngineType = DatabaseEngineId | 'unknown';

/**
 * Error category for sanitized error tracking.
 *
 * The first block is the original coarse taxonomy — those values must stay
 * stable (existing dashboards filter on them). The refinement blocks were
 * added because `unknown` dominated both `connection:error` and
 * `feature:query_error`; `classifyDbError` (dbErrorClassifier.ts) maps
 * per-engine error signatures onto them, falling back to `categorizeError`
 * below and only then to `unknown`.
 */
export type AnalyticsErrorCategory =
  // Original coarse buckets (telemetry continuity — do not rename)
  | 'auth_failed'
  | 'timeout'
  | 'network'
  | 'ssl'
  | 'permission'
  | 'not_found'
  | 'unknown'
  // Connection-failure refinements
  | 'tls_cert_untrusted'
  | 'tls_required'
  | 'tls_unsupported'
  | 'dns_not_found'
  | 'conn_refused'
  | 'host_unreachable'
  | 'db_not_found'
  | 'instance_not_found'
  | 'firewall_blocked'
  | 'ssh_tunnel'
  | 'file_not_found'
  // Server too old for the bundled driver to talk to at all (e.g. Oracle
  // NJS-138: thin mode refuses servers older than 12.1) — an honest
  // explanation, never a fix chip: there is no client-side remedy.
  | 'unsupported_server'
  // Query-failure refinements
  | 'syntax_error'
  | 'object_not_found'
  | 'constraint_violation'
  | 'conversion_error'
  | 'lock_contention'
  | 'connection_lost'
  | 'cancelled';

/**
 * Every workspace tab type id — the closed vocabulary of `activeTabType` on
 * the hang events. It lives here, not beside `Tab`, because the electron build
 * compiles this file (main validates against it) and cannot compile the
 * workspace types. It must equal `Tab['type']` exactly: a `Tab['type']` value
 * missing here fails `yarn typecheck` in `uiContextPublisher.ts`, and
 * `uiContextPublisher.test.ts` pins both directions with `expectTypeOf`.
 */
export const UI_CONTEXT_TAB_TYPES = [
  'query', 'table-editor', 'table-explorer', 'plan', 'table-designer', 'security',
  'schema-compare', 'data-compare', 'dependency-viewer', 'schema-overview',
  'spatial-explorer', 'data-profile', 'start-page', 'notebook', 'session-browser',
  'tablespace-manager', 'oracle-object-detail', 'data-pump', 'instance-monitor',
  'filegroup-manager', 'pl-sql-debugger', 'job-scheduler', 'plsql-editor',
  'package-explorer', 'references-tab', 'database-blueprint',
  'cross-engine-migrate', 'dashboard', 'data-import', 'deadlock-analyzer', 'query-store',
  'storage-explorer',
] as const;

export type UiContextTabType = (typeof UI_CONTEXT_TAB_TYPES)[number];

/**
 * Every engine id — the closed vocabulary of `activeEngine` on the hang
 * events. Keyed by `DatabaseEngineId`, so a new engine that is not added here
 * fails to compile.
 */
export const UI_CONTEXT_ENGINE_IDS: Readonly<Record<DatabaseEngineId, true>> = {
  mssql: true,
  pgsql: true,
  sqlite: true,
  mysql: true,
  oracle: true,
  kusto: true,
  azurestorage: true,
  mock: true,
};

/**
 * The renderer's UI context as it crosses to main (`analytics.setUiContext`),
 * which attaches the last one to the hang events — a frozen renderer cannot
 * report. Built by `uiContextPublisher.ts`, validated against the two
 * vocabularies above and held by `electron/analytics/rendererUiContext.ts`.
 */
export type RendererUiContext = {
  tabsBucket: CountBucket;
  /** The active workspace tab's type id, or `'none'`. */
  activeTabType: UiContextTabType | 'none';
  /** The active tab's connection engine, or `'none'`. */
  activeEngine: DatabaseEngineId | 'none';
  /** True from session-restore start until the restore completes or is cancelled. */
  restoreInProgress: boolean;
};

/**
 * Additional data attached to events.
 * Matches the ElectronAPI signature - all values must be defined.
 */
export type AnalyticsEventData = Record<string, string | number | boolean>;

/** Track function signature */
export type TrackFunction = (
  eventName: string,
  data?: AnalyticsEventData
) => void;

/**
 * Sanitize an error message into a safe category for analytics.
 * NEVER includes sensitive data: server names, file paths, SQL, stack traces.
 *
 * @param error - The error message or Error object
 * @returns A safe error category string
 */
export function categorizeError(error: string | Error | unknown): AnalyticsErrorCategory {
  const message = error instanceof Error
    ? error.message.toLowerCase()
    : String(error).toLowerCase();

  // Auth/Login failures
  if (
    message.includes('login failed') ||
    message.includes('authentication') ||
    message.includes('invalid credentials') ||
    message.includes('password') ||
    message.includes('access denied') ||
    message.includes('unauthorized')
  ) {
    return 'auth_failed';
  }

  // Timeout
  if (
    message.includes('timeout') ||
    message.includes('timed out') ||
    message.includes('etimedout')
  ) {
    return 'timeout';
  }

  // Network errors
  if (
    message.includes('network') ||
    message.includes('econnrefused') ||
    message.includes('econnreset') ||
    message.includes('enotfound') ||
    message.includes('socket') ||
    message.includes('connection refused') ||
    message.includes('unable to connect') ||
    message.includes('host not found') ||
    message.includes('getaddrinfo')
  ) {
    return 'network';
  }

  // SSL/TLS errors
  if (
    message.includes('ssl') ||
    message.includes('tls') ||
    message.includes('certificate') ||
    message.includes('cert_')
  ) {
    return 'ssl';
  }

  // Permission errors
  if (
    message.includes('permission') ||
    message.includes('eperm') ||
    message.includes('eacces') ||
    message.includes('forbidden')
  ) {
    return 'permission';
  }

  // Not found errors
  if (
    message.includes('not found') ||
    message.includes('does not exist') ||
    message.includes('enoent') ||
    message.includes('cannot find')
  ) {
    return 'not_found';
  }

  return 'unknown';
}
